import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText,
  Calendar,
  BookOpen,
  Layout,
  Landmark,
  Target,
  TrendingUp,
  Clock,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Download,
  Loader2,
  ChevronDown,
  Database
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

export const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentProposals, setRecentProposals] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Monthly Summary State
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [selectedTimeline, setSelectedTimeline] = useState("monthly");
  const [includeSupervisorReport, setIncludeSupervisorReport] = useState(false);
  const [staffName, setStaffName] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [docxLoading, setDocxLoading] = useState(false);
  const [activeReportTab, setActiveReportTab] = useState("complete");
  
  // Backup State
  const [backupLoading, setBackupLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, proposalsRes, eventsRes, goalsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/proposals`),
        axios.get(`${API}/events`),
        axios.get(`${API}/yearly-goals`)
      ]);
      setStats(statsRes.data);
      
      // Sort proposals by created_at descending (most recent first)
      const sortedProposals = [...proposalsRes.data].sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });
      setRecentProposals(sortedProposals.slice(0, 5));
      
      // Sort events by event_date or created_at descending (most recent first)
      const sortedEvents = [...eventsRes.data].sort((a, b) => {
        const dateA = new Date(a.event_date || a.date || a.created_at || 0);
        const dateB = new Date(b.event_date || b.date || b.created_at || 0);
        return dateB - dateA;
      });
      setRecentEvents(sortedEvents.slice(0, 5));
      
      setGoals(goalsRes.data.slice(0, 4));
      
      // Filter upcoming events (events with event_date in the future)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const upcoming = eventsRes.data
        .filter(event => {
          if (!event.event_date) return false;
          const eventDate = new Date(event.event_date);
          return eventDate >= today;
        })
        .sort((a, b) => new Date(a.event_date) - new Date(b.event_date))
        .slice(0, 5);
      setUpcomingEvents(upcoming);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateMonthlySummary = async () => {
    setSummaryLoading(true);
    setSummaryData(null);
    setReportData(null);
    try {
      const response = await axios.post(`${API}/staff/comprehensive-report`, {
        timeline: selectedTimeline,
        year: selectedYear,
        month: selectedMonth,
        quarter: selectedQuarter,
        staff_name: staffName,
        include_supervisor_report: includeSupervisorReport
      });
      setReportData(response.data);
      toast.success("Report generated successfully");
    } catch (error) {
      console.error("Error generating report:", error);
      toast.error("Failed to generate report");
    } finally {
      setSummaryLoading(false);
    }
  };

  const downloadSummaryPdf = async () => {
    setPdfLoading(true);
    try {
      const response = await axios.post(`${API}/ai/monthly-summary/pdf`, {
        year: selectedYear,
        month: selectedMonth,
        staff_name: staffName
      }, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Library_Monthly_Summary_${MONTHS[selectedMonth - 1].label}_${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to download PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadSummaryDocx = async () => {
    setDocxLoading(true);
    try {
      const response = await axios.post(`${API}/staff/monthly-report/docx?year=${selectedYear}&month=${selectedMonth}`, {}, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Monthly_Self_Report_${MONTHS[selectedMonth - 1].label}_${selectedYear}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Word document downloaded successfully");
    } catch (error) {
      console.error("Error downloading Word document:", error);
      toast.error("Failed to download Word document");
    } finally {
      setDocxLoading(false);
    }
  };

  const downloadBackup = async (format) => {
    setBackupLoading(true);
    try {
      const response = await axios.get(`${API}/backup/${format}`, {
        responseType: 'blob'
      });
      
      const extension = format === 'json' ? 'json' : 'xlsx';
      const mimeType = format === 'json' 
        ? 'application/json' 
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `library_backup_${new Date().toISOString().split('T')[0]}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Backup downloaded successfully as ${extension.toUpperCase()}`);
    } catch (error) {
      console.error("Error downloading backup:", error);
      toast.error("Failed to download backup");
    } finally {
      setBackupLoading(false);
    }
  };

  const formatSummaryText = (text) => {
    // Convert markdown-like formatting to JSX
    return text.split('\n').map((line, index) => {
      if (!line.trim()) return <br key={index} />;
      
      // Headers
      if (line.startsWith('## ')) {
        return <h3 key={index} className="text-lg font-semibold mt-4 mb-2 text-primary">{line.replace('## ', '')}</h3>;
      }
      if (line.startsWith('# ')) {
        return <h2 key={index} className="text-xl font-bold mt-4 mb-2 text-primary">{line.replace('# ', '')}</h2>;
      }
      
      // Bold text
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={index} className="font-semibold mt-2">{line.replace(/\*\*/g, '')}</p>;
      }
      
      // Bullet points
      if (line.startsWith('- ') || line.startsWith('* ')) {
        return <li key={index} className="ml-4 text-sm">{line.substring(2)}</li>;
      }
      
      // Numbered list
      if (/^\d+\.\s/.test(line)) {
        return <li key={index} className="ml-4 text-sm list-decimal">{line.replace(/^\d+\.\s/, '')}</li>;
      }
      
      return <p key={index} className="text-sm">{line}</p>;
    });
  };

  const statCards = [
    { label: "Program Proposals", value: stats?.total_proposals || 0, icon: FileText, color: "text-blue-600", path: "/proposals" },
    { label: "Events", value: stats?.total_events || 0, icon: Calendar, color: "text-amber-600", path: "/events" },
    { label: "Books", value: stats?.total_books || 0, icon: BookOpen, color: "text-emerald-600", path: "/books" },
    { label: "Book Displays", value: stats?.total_displays || 0, icon: Layout, color: "text-purple-600", path: "/book-displays" },
    { label: "Literacy Stations", value: stats?.total_stations || 0, icon: Landmark, color: "text-rose-600", path: "/literacy-stations" },
    { label: "Yearly Goals", value: stats?.total_goals || 0, icon: Target, color: "text-cyan-600", path: "/yearly-goals" },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-stone-100 text-stone-600",
      submitted: "bg-blue-100 text-blue-700",
      under_review: "bg-amber-100 text-amber-700",
      approved: "bg-emerald-100 text-emerald-700",
      rejected: "bg-red-100 text-red-700"
    };
    return styles[status] || styles.draft;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="animate-fade-in flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-foreground">
            Dashboard
          </h1>
          <p className="text-muted-foreground mt-2">
            Welcome to Circulate
          </p>
        </div>
        <div className="flex gap-2">
          <Select onValueChange={(value) => downloadBackup(value)} disabled={backupLoading}>
            <SelectTrigger className="w-[160px]" data-testid="backup-dropdown">
              {backupLoading ? (
                <span className="flex items-center">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Backing up...
                </span>
              ) : (
                <span className="flex items-center">
                  <Download className="mr-2 h-4 w-4" />
                  Backup Data
                </span>
              )}
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="excel">Download Excel</SelectItem>
              <SelectItem value="json">Download JSON</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            onClick={() => setSummaryDialogOpen(true)}
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
            data-testid="generate-summary-btn"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Generate Monthly Summary
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Link to={stat.path} key={stat.label}>
              <Card
                className="card-hover cursor-pointer animate-slide-up border-stone-200"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        {stat.label}
                      </p>
                      <p className="text-2xl font-semibold mt-1">{stat.value}</p>
                    </div>
                    <Icon className={`h-8 w-8 ${stat.color} opacity-80`} strokeWidth={1.5} />
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-stone-200" data-testid="pending-proposals-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-amber-50">
                <Clock className="h-6 w-6 text-amber-600" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Review</p>
                <p className="text-2xl font-semibold">{stats?.pending_proposals || 0}</p>
                <p className="text-xs text-muted-foreground">proposals</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200" data-testid="active-stations-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-emerald-50">
                <CheckCircle className="h-6 w-6 text-emerald-600" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Stations</p>
                <p className="text-2xl font-semibold">{stats?.active_stations || 0}</p>
                <p className="text-xs text-muted-foreground">literacy stations</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200" data-testid="upcoming-events-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-50">
                <TrendingUp className="h-6 w-6 text-blue-600" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Events</p>
                <p className="text-2xl font-semibold">{stats?.upcoming_events || 0}</p>
                <p className="text-xs text-muted-foreground">recorded</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Proposals */}
        <Card className="border-stone-200" data-testid="recent-proposals">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-xl">Recent Proposals</CardTitle>
              <Link to="/proposals">
                <Button variant="ghost" size="sm" data-testid="view-all-proposals">
                  View All <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {recentProposals.length === 0 ? (
              <p className="text-muted-foreground text-sm py-4">No proposals yet</p>
            ) : (
              <div className="space-y-3">
                {recentProposals.map((proposal) => (
                  <div
                    key={proposal.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{proposal.program_name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {proposal.target_audience}
                      </p>
                    </div>
                    <Badge className={getStatusBadge(proposal.status)}>
                      {proposal.status.replace("_", " ")}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Events */}
        <Card className="border-stone-200" data-testid="recent-events">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-xl">Recent Events</CardTitle>
              <Link to="/events">
                <Button variant="ghost" size="sm" data-testid="view-all-events">
                  View All <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {recentEvents.length === 0 ? (
              <p className="text-muted-foreground text-sm py-4">No events yet</p>
            ) : (
              <div className="space-y-3">
                {recentEvents.map((event) => (
                  <div
                    key={event.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{event.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {event.date} • {event.participants_count} participants
                      </p>
                    </div>
                    <Badge variant="outline" className="capitalize">
                      {event.event_type}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Events */}
      <Card className="border-stone-200 border-l-4 border-l-blue-500" data-testid="upcoming-events">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-500" />
              <CardTitle className="font-heading text-xl">Upcoming Events</CardTitle>
            </div>
            <Link to="/events">
              <Button variant="ghost" size="sm" data-testid="view-all-upcoming">
                View All <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
          <CardDescription>Events scheduled for the future</CardDescription>
        </CardHeader>
        <CardContent>
          {upcomingEvents.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">No upcoming events scheduled</p>
              <Link to="/events">
                <Button variant="outline" size="sm" className="mt-3">
                  Schedule an Event
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcomingEvents.map((event) => {
                const eventDate = new Date(event.event_date);
                const daysUntil = Math.ceil((eventDate - new Date()) / (1000 * 60 * 60 * 24));
                return (
                  <div
                    key={event.id}
                    className="p-4 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <Badge 
                        variant="outline" 
                        className={daysUntil <= 7 ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-blue-100 text-blue-700 border-blue-200"}
                      >
                        {daysUntil === 0 ? "Today" : daysUntil === 1 ? "Tomorrow" : `In ${daysUntil} days`}
                      </Badge>
                      <Badge variant="outline" className="capitalize text-xs">
                        {event.event_type}
                      </Badge>
                    </div>
                    <h4 className="font-semibold text-sm mb-1 line-clamp-2">{event.title}</h4>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                      <Calendar className="h-3 w-3" />
                      <span>{eventDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                      {event.time && <span>• {event.time}</span>}
                    </div>
                    {event.location && (
                      <p className="text-xs text-muted-foreground truncate">
                        📍 {event.location}
                      </p>
                    )}
                    {event.priority && (
                      <Badge 
                        className={`mt-2 text-xs ${
                          event.priority === 'high' ? 'bg-red-100 text-red-700' :
                          event.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {event.priority} priority
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Goals Progress */}
      {goals.length > 0 && (
        <Card className="border-stone-200" data-testid="goals-progress">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-xl">Yearly Goals Progress</CardTitle>
              <Link to="/yearly-goals">
                <Button variant="ghost" size="sm" data-testid="view-all-goals">
                  View All <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {goals.map((goal) => {
                const progress = goal.target_value > 0 
                  ? Math.round((goal.current_value / goal.target_value) * 100) 
                  : 0;
                return (
                  <div key={goal.id} className="p-4 rounded-lg bg-muted/30">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-sm">{goal.title}</p>
                      <span className="text-xs font-mono text-muted-foreground">
                        {goal.current_value}/{goal.target_value} {goal.unit}
                      </span>
                    </div>
                    <Progress value={progress} className="h-2" />
                    <p className="text-xs text-muted-foreground mt-1">{progress}% complete</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Assistant CTA */}
      <Card className="ai-highlight border-amber-200" data-testid="ai-assistant-cta">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-amber-100">
                <Target className="h-6 w-6 text-amber-600" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold">AI Assistant</h3>
                <p className="text-sm text-muted-foreground">
                  Get summaries, recommendations, and to-do lists powered by AI
                </p>
              </div>
            </div>
            <Link to="/ai-assistant">
              <Button data-testid="go-to-ai-assistant">
                Try AI Assistant <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Summary Dialog */}
      <Dialog open={summaryDialogOpen} onOpenChange={setSummaryDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              Staff Activity Report
            </DialogTitle>
            <DialogDescription>
              Generate a comprehensive report of your events organized for review
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Staff Name Input */}
            <div>
              <label className="text-sm font-medium mb-1 block">Your Name *</label>
              <input
                type="text"
                value={staffName}
                onChange={(e) => setStaffName(e.target.value)}
                placeholder="Enter your name (e.g., John Smith)"
                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="staff-name-input"
              />
            </div>
            
            {/* Timeline Selection */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Timeline</label>
                <Select value={selectedTimeline} onValueChange={setSelectedTimeline}>
                  <SelectTrigger data-testid="timeline-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ytd">Jan to Today</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-1 block">Year</label>
                <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                  <SelectTrigger data-testid="year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map((year) => (
                      <SelectItem key={year} value={String(year)}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedTimeline === "monthly" && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Month</label>
                  <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                    <SelectTrigger data-testid="month-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((month) => (
                        <SelectItem key={month.value} value={String(month.value)}>
                          {month.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {selectedTimeline === "quarterly" && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Quarter</label>
                  <Select value={String(selectedQuarter)} onValueChange={(v) => setSelectedQuarter(parseInt(v))}>
                    <SelectTrigger data-testid="quarter-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Q1 (Jan-Mar)</SelectItem>
                      <SelectItem value="2">Q2 (Apr-Jun)</SelectItem>
                      <SelectItem value="3">Q3 (Jul-Sep)</SelectItem>
                      <SelectItem value="4">Q4 (Oct-Dec)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="flex items-end">
                <Button 
                  onClick={generateMonthlySummary} 
                  disabled={summaryLoading || !staffName.trim()}
                  className="bg-purple-600 hover:bg-purple-700 w-full"
                  data-testid="generate-btn"
                >
                  {summaryLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Generate
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            {/* Supervisor Report Option */}
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <input
                type="checkbox"
                id="supervisor-report"
                checked={includeSupervisorReport}
                onChange={(e) => setIncludeSupervisorReport(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="supervisor-report" className="text-sm">
                Include supervisor comparison report (AI-generated objective evaluation)
              </label>
            </div>
            
            {/* Report Results */}
            {reportData && (
              <div className="space-y-4 animate-fade-in">
                {/* Header */}
                <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border">
                  <h3 className="text-xl font-semibold text-purple-800">{reportData.staff_name}</h3>
                  <p className="text-sm text-purple-600">{reportData.timeline_label}</p>
                </div>
                
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card className="bg-emerald-50 border-emerald-200">
                    <CardContent className="p-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-emerald-600" />
                        <p className="text-xs text-emerald-600 font-medium">Complete</p>
                      </div>
                      <p className="text-2xl font-bold text-emerald-700">{reportData.summary_stats.complete}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="p-3">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-amber-600" />
                        <p className="text-xs text-amber-600 font-medium">In Progress</p>
                      </div>
                      <p className="text-2xl font-bold text-amber-700">{reportData.summary_stats.in_progress}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-3">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-blue-600" />
                        <p className="text-xs text-blue-600 font-medium">New This Month</p>
                      </div>
                      <p className="text-2xl font-bold text-blue-700">{reportData.summary_stats.new_this_month}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-rose-50 border-rose-200">
                    <CardContent className="p-3">
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-rose-600" />
                        <p className="text-xs text-rose-600 font-medium">Priorities</p>
                      </div>
                      <p className="text-2xl font-bold text-rose-700">{reportData.summary_stats.upcoming_priorities}</p>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Event Sections Tabs */}
                <div className="border rounded-xl overflow-hidden">
                  <div className="flex border-b bg-muted/30">
                    <button
                      onClick={() => setActiveReportTab("complete")}
                      className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${activeReportTab === "complete" ? "bg-emerald-100 text-emerald-700 border-b-2 border-emerald-500" : "text-muted-foreground hover:bg-muted"}`}
                    >
                      Complete <Badge variant="outline" className="ml-1 text-xs">{reportData.complete_events.length}</Badge>
                    </button>
                    <button
                      onClick={() => setActiveReportTab("progress")}
                      className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${activeReportTab === "progress" ? "bg-amber-100 text-amber-700 border-b-2 border-amber-500" : "text-muted-foreground hover:bg-muted"}`}
                    >
                      In Progress <Badge variant="outline" className="ml-1 text-xs">{reportData.in_progress_events.length}</Badge>
                    </button>
                    <button
                      onClick={() => setActiveReportTab("new")}
                      className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${activeReportTab === "new" ? "bg-blue-100 text-blue-700 border-b-2 border-blue-500" : "text-muted-foreground hover:bg-muted"}`}
                    >
                      New Entries <Badge variant="outline" className="ml-1 text-xs">{reportData.new_entries.length}</Badge>
                    </button>
                    <button
                      onClick={() => setActiveReportTab("priorities")}
                      className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${activeReportTab === "priorities" ? "bg-rose-100 text-rose-700 border-b-2 border-rose-500" : "text-muted-foreground hover:bg-muted"}`}
                    >
                      Priorities <Badge variant="outline" className="ml-1 text-xs">{reportData.priorities_next_4_weeks.length}</Badge>
                    </button>
                  </div>
                  
                  <div className="p-4 max-h-64 overflow-y-auto">
                    {activeReportTab === "complete" && (
                      <div className="space-y-2">
                        {reportData.complete_events.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">No completed events in this period</p>
                        ) : (
                          reportData.complete_events.map((event, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-emerald-50/50 rounded-lg border border-emerald-100">
                              <div>
                                <p className="font-medium text-sm">{event.title}</p>
                                <p className="text-xs text-muted-foreground">
                                  {event.date} • {event.event_type}
                                  {event.is_positive && <Badge className="ml-2 text-xs bg-emerald-100 text-emerald-700">Positive</Badge>}
                                </p>
                              </div>
                              <CheckCircle className="h-4 w-4 text-emerald-500" />
                            </div>
                          ))
                        )}
                        <p className="text-xs text-muted-foreground italic mt-2">Sorted: Newest to Oldest</p>
                      </div>
                    )}
                    
                    {activeReportTab === "progress" && (
                      <div className="space-y-2">
                        {reportData.in_progress_events.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">No events in progress</p>
                        ) : (
                          reportData.in_progress_events.map((event, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-amber-50/50 rounded-lg border border-amber-100">
                              <div>
                                <p className="font-medium text-sm">{event.title}</p>
                                <p className="text-xs text-muted-foreground">
                                  {event.date} • {event.event_type}
                                  {event.priority && <Badge className="ml-2 text-xs" variant="outline">{event.priority}</Badge>}
                                </p>
                              </div>
                              <Clock className="h-4 w-4 text-amber-500" />
                            </div>
                          ))
                        )}
                        <p className="text-xs text-muted-foreground italic mt-2">Sorted: Oldest to Newest (longest running first)</p>
                      </div>
                    )}
                    
                    {activeReportTab === "new" && (
                      <div className="space-y-2">
                        {reportData.new_entries.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">No new entries since the 1st of this month</p>
                        ) : (
                          reportData.new_entries.map((event, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-blue-50/50 rounded-lg border border-blue-100">
                              <div>
                                <p className="font-medium text-sm">{event.title}</p>
                                <p className="text-xs text-muted-foreground">
                                  Created: {new Date(event.created_at).toLocaleDateString()} • {event.status}
                                </p>
                              </div>
                              <TrendingUp className="h-4 w-4 text-blue-500" />
                            </div>
                          ))
                        )}
                        <p className="text-xs text-muted-foreground italic mt-2">Events created since the 1st of the current month</p>
                      </div>
                    )}
                    
                    {activeReportTab === "priorities" && (
                      <div className="space-y-2">
                        {reportData.priorities_next_4_weeks.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">No upcoming priorities in the next 4 weeks</p>
                        ) : (
                          reportData.priorities_next_4_weeks.map((event, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-rose-50/50 rounded-lg border border-rose-100">
                              <div>
                                <p className="font-medium text-sm">{event.title}</p>
                                <p className="text-xs text-muted-foreground">
                                  Due: {event.date}
                                  {event.priority === "high" && <Badge className="ml-2 text-xs bg-red-100 text-red-700">High Priority</Badge>}
                                  {event.priority === "medium" && <Badge className="ml-2 text-xs bg-amber-100 text-amber-700">Medium</Badge>}
                                </p>
                              </div>
                              <Target className="h-4 w-4 text-rose-500" />
                            </div>
                          ))
                        )}
                        <p className="text-xs text-muted-foreground italic mt-2">Sorted by event due date (next 4 weeks)</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* AI Staff Summary */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-purple-500" />
                      Staff Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="prose prose-sm max-w-none">
                    <div className="bg-purple-50/50 rounded-lg p-4 text-sm whitespace-pre-wrap">
                      {reportData.ai_summary}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Supervisor Summary (if included) */}
                {reportData.supervisor_summary && (
                  <Card className="border-amber-200">
                    <CardHeader className="pb-2 bg-amber-50/50">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="h-5 w-5 text-amber-600" />
                        Supervisor Review Summary
                      </CardTitle>
                      <CardDescription>AI-generated objective evaluation for comparison</CardDescription>
                    </CardHeader>
                    <CardContent className="prose prose-sm max-w-none">
                      <div className="bg-amber-50/30 rounded-lg p-4 text-sm whitespace-pre-wrap">
                        {reportData.supervisor_summary}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Download Buttons */}
                <div className="flex justify-end gap-2">
                  <Button 
                    onClick={downloadSummaryDocx} 
                    disabled={docxLoading}
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    {docxLoading ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <FileText className="mr-2 h-4 w-4" />
                    )}
                    Download Word
                  </Button>
                  <Button 
                    onClick={downloadSummaryPdf} 
                    disabled={pdfLoading}
                    variant="outline"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  >
                    {pdfLoading ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="mr-2 h-4 w-4" />
                    )}
                    Download PDF
                  </Button>
                </div>
              </div>
            )}
            
            {/* Initial State */}
            {!reportData && !summaryLoading && (
              <div className="text-center py-12 bg-muted/20 rounded-lg space-y-4">
                <Sparkles className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  Enter your name, select a timeline, then click "Generate" to create your activity report
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
